import { AbsgArrayHelper } from '@absg/angular-common';
import { DateTimeHelper } from '@absg/angular-platform';
import { PickListItem } from '@absg/nim-client-shared';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CalendarMonthViewDay } from 'angular-calendar';
import { StationInfoService } from 'core.services/station-info.service';
import { FutureMedOrderOptions } from 'dispense.models/future-med-order-options.model';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { EmrService } from 'shared.root/services/emr.service';

import { ICalendarPickerComponent } from '../../models/calendar-picker-component.interface';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: '[nc-calendar-picker]',
  templateUrl: './calendar-picker.component.html',
  standalone: false,
})

export class CalendarPickerComponent implements OnInit, OnChanges, OnDestroy, ICalendarPickerComponent {
  @Input() selectedDate: Date;
  @Input() selectedClinicItem: PickListItem;
  @Input() isModalOpen: Observable<boolean>;
  @Input() minDateString: string = null;
  @Input() maxDateString: string = null;
  @Input() header: string;
  @Input() futureMedOrdersMode: boolean;
  @Input() showDefaultMonthOnly = false; /* this occurs when there is an error in the date field.
  turns off autoSelectDay, no selected date and defaults to current month */

  @Output() cancelModal = new EventEmitter();
  @Output() saveModal = new EventEmitter<Date>();
  @Output() clearDateModal = new EventEmitter();
  @Output() saveFutureMedOrderOptions = new EventEmitter<FutureMedOrderOptions>();

  isBtnNextDisabled = false;
  isBtnPreviousDisabled = false;

  datePickerModalOpenStatus = false;
  selectedDay: CalendarMonthViewDay;
  viewDate: Date;

  private minDate: Date;
  private maxDate: Date;

  private modalOpenSubscription: Subscription;

  isClinicModalOpen$ = new BehaviorSubject<boolean>(false);
  clinicList: PickListItem[] = [];

  constructor(
    private changeDetection: ChangeDetectorRef,
    private stationInfoService: StationInfoService,
    private emrService: EmrService,
  ) {
  }

  // Angular Calendar requires this format of 'body' - DO NOT RENAME
  dayModifier({ body }: { body: CalendarMonthViewDay[] }): void {
    body.forEach(day => {
      day = this.formatCalendarDay(day);
    });
    this.dateOrViewChanged();
  }

  formatCalendarDay(day: CalendarMonthViewDay): CalendarMonthViewDay {
    if (!this.dateIsValid(day.date)) {
      day.cssClass = 'cal-disabled';
    } else if (DateTimeHelper.isSame(day.date, this.selectedDate, 'day')) {
      if (!this.showDefaultMonthOnly) {
        this.selectedDay = day;
        day.cssClass = 'cal-day-selected';
      }
    }
    return day;
  }

  ngOnInit(): void {
    if (!this.header) {
      this.header = '';
    }

    this.minDate = new Date(this.minDateString);
    this.maxDate = new Date(this.maxDateString);

    this.modalOpenSubscription = this.isModalOpen.subscribe((isModalOpen) => {
      this.datePickerModalOpenStatus = isModalOpen;
      this.changeDetection.markForCheck();
      if (this.datePickerModalOpenStatus) {
        this.initDate();
        if (this.futureMedOrdersMode) {
          this.loadClinics();
        }
      }
    });
    this.initDate();
  }

  ngOnDestroy(): void {
    if (this.modalOpenSubscription) {
      this.modalOpenSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (propName === 'selectedDate') {
        const change = changes[propName];
        this.validateNewDate(change.currentValue);
      } else if (propName === 'minDateString') {
        const change = changes[propName];
        this.minDate = new Date(change.currentValue);
      }
    }
  }

  validateNewDate(currentValue: string): void {
    const newDate = new Date(currentValue);

    if (JSON.stringify(currentValue) !== 'null' && this.dateIsValid(newDate)) {
      if (this.selectedDay) {
        delete this.selectedDay.cssClass;
      }

      this.selectedDate = newDate;
      this.changeDate(this.selectedDate);
    }
  }

  // Methods
  changeDate(date: Date): void {
    this.viewDate = this.formatDate(date);
    this.dateOrViewChanged();
  }

  increment(): void {
    this.changeDate(DateTimeHelper.addMonths(this.viewDate, 1));
  }

  decrement(): void {
    this.changeDate(DateTimeHelper.subMonths(this.viewDate, 1));
  }

  dateIsValid(date: Date): boolean {
    if (date.toString() === 'Invalid Date') {
      return false;
    }

    return date >= this.minDate && date <= this.maxDate;
  }

  dateOrViewChanged(): void {
    this.isBtnPreviousDisabled = !this.dateIsValid(DateTimeHelper.endOfMonth(DateTimeHelper.subMonths(this.viewDate, 1)));
    this.isBtnNextDisabled = !this.dateIsValid(DateTimeHelper.startOfMonth(DateTimeHelper.addMonths(this.viewDate, 1)));
    if (this.viewDate < this.minDate) {
      this.changeDate(this.minDate);
    } else if (this.viewDate > this.maxDate) {
      this.changeDate(this.maxDate);
    }
  }

  // Events
  onCancel(event: Event): void {
    event.preventDefault();
    this.cancelModal.emit();
    if (this.futureMedOrdersMode) {
      this.selectedDate = this.viewDate;
      this.selectedClinicItem = null;
    }
  }

  onClear(): void {
    this.clearDateModal.emit();
  }

  onSave(day: CalendarMonthViewDay): void {
    if (!this.dateIsValid(day.date)) {
      return;
    }

    if (this.selectedDay) {
      delete this.selectedDay.cssClass;
    }
    day.cssClass = 'cal-day-selected';
    this.selectedDay = day;
    this.showDefaultMonthOnly = false;

    if (this.futureMedOrdersMode) {
      this.selectedDate = day.date;
    } else {
      this.saveModal.emit(day.date);
    }
  }

  onSelectClinicClick(): void {
    this.isClinicModalOpen$.next(true);
  }

  onClinicChanged(item: PickListItem): void {
    this.selectedClinicItem = item;
  }

  onClinicsClear(): void {
    this.selectedClinicItem = this.getDefaultClinic();
  }

  isSaveFutureMedOrderOptionsEnabled(): boolean {
    return !AbsgArrayHelper.isNil(this.selectedDate) && !!(this.selectedClinicItem);
  }

  onSaveFutureMedOrderOptions(): void {
    const futureMedOrderOptions = new FutureMedOrderOptions(this.selectedDay.date, this.selectedClinicItem.id, this.selectedClinicItem.text);
    this.saveFutureMedOrderOptions.emit(futureMedOrderOptions);
    this.selectedClinicItem = null;
  }

  private getDefaultClinic(): PickListItem {
    const currentClinic = this.stationInfoService.practiceClinicDetail.getValue();
    return AbsgArrayHelper.find(this.clinicList, clinic => clinic.id === currentClinic.clinicId);
  }

  private formatDate(date: Date): Date {
    return new Date(DateTimeHelper.formatDateTime(date, DateTimeHelper.defaultDateFormat));
  }

  private initDate(): void {
    this.viewDate = this.formatDate(this.selectedDate);
    this.dateOrViewChanged();
  }

  private loadClinics(): void {
    forkJoin([
      this.stationInfoService.getClinicsInfoByPracticeId(),
      this.emrService.getFutureMedOrdersClinics(),
    ]).pipe(take(1),
      map(([practiceClinics, futureMedOrdersClinics]) => AbsgArrayHelper.map(AbsgArrayHelper.filter(practiceClinics,
        practiceClinic => AbsgArrayHelper.some(futureMedOrdersClinics, fmoc => fmoc === practiceClinic.clinicId)),
        clinic => new PickListItem(clinic.clinicId, clinic.clinicCurrentName))),
    ).subscribe(result => {
      this.clinicList = result;
      this.selectedClinicItem = this.selectedClinicItem ?? this.getDefaultClinic();
    });
  }
}
